import {createSelector} from "@reduxjs/toolkit";
import {BACKGROUND_COLOR_IMAGE_DEFAULT} from "appConstants";
import {maybe} from "types/basic";
import {Configuration, ConfigurationsState} from "types/configurations";
import {ConfigurationKey, ProductSummary} from "@pinch-financial/pinch-ui-components";
import {IconDisplay} from "types/iconDisplay";
import {State} from "types/store";
import {isConfigurationValueTrue} from "util/configUtil";
import {toLanguageType} from "util/languageUtil";

export const get = (rootState: State): ConfigurationsState => rootState.whiteLabelConfigurations;

export const getIsNavigationMenuEnabled = createSelector(get, (configState) => {
  return configState?.some(
    (configuration: Configuration) =>
      configuration?.key === ConfigurationKey.WHITELABEL_NAVIGATION_MENU &&
      isConfigurationValueTrue(configuration)
  );
});

export const getIsPurchaseTimeframePageActive = createSelector(get, (configState) => {
  return configState?.some(
    (configuration: Configuration) =>
      configuration?.key === ConfigurationKey.WHITELABEL_ACTIVE_PAGES_PURCHASE_TIMEFRAME &&
      isConfigurationValueTrue(configuration)
  );
});

export const getIsProvinceSelectPageActive = createSelector(get, (configState) => {
  return configState?.some(
    (configuration: Configuration) =>
      configuration?.key === ConfigurationKey.WHITELABEL_ACTIVE_PAGES_PROVINCE_SELECT &&
      isConfigurationValueTrue(configuration)
  );
});

export const getLanguage = createSelector(get, (configState) => {
  return toLanguageType(
    configState?.find(
      (configuration: Configuration) =>
        configuration?.key === ConfigurationKey.ORGANIZATION_LANGUAGE
    )?.value
  );
});

export const getOrganizationProductStyleOverride = createSelector(get, (configrations) => {
  return configrations?.find(
    (config: Configuration) => config?.key === ConfigurationKey.ORGANIZATION_PRODUCT_STYLE_OVERRIDE
  );
});

export const getIsOrganizationProductStyleOverride = createSelector(
  getOrganizationProductStyleOverride,
  (config) => config?.value === "true"
);

export const getIconDisplayMapper = createSelector(
  getIsOrganizationProductStyleOverride,
  (isUsingProductStyle) =>
    (productSummary: maybe<ProductSummary>): IconDisplay => {
      if (!isUsingProductStyle) {
        console.warn(
          "No longer using product style override config - product style is now the default"
        );
      }
      const iconUrl = `${window._env_.REACT_APP_IMGIX_DOMAIN}/${productSummary?.productIconFileName}`;

      return {
        id: productSummary?.productPublicId,
        icon: productSummary?.productIconFileName ? iconUrl : undefined,
        backgroundColor: productSummary?.productBackgroundColor || BACKGROUND_COLOR_IMAGE_DEFAULT,
        name: productSummary?.productName || undefined,
      };
    }
);

export const getIconDisplayMapperWithPrimaryLogoOverride = createSelector(
  () =>
    (productSummary: ProductSummary): IconDisplay => {
      let iconFileName = productSummary?.productIconFileName;
      let backgroundColor = productSummary?.productBackgroundColor;

      if (productSummary?.useFinancialInstitutionPrimaryLogo) {
        iconFileName = productSummary?.financialInstitutionLogo;
        backgroundColor = productSummary?.financialInstitutionBackgroundColor;
      }

      return {
        id: productSummary.financialInstitutionPublicId,
        icon: productSummary?.productIconFileName
          ? `${window._env_.REACT_APP_IMGIX_DOMAIN}/${iconFileName}`
          : undefined,
        backgroundColor: backgroundColor || BACKGROUND_COLOR_IMAGE_DEFAULT,
        name: productSummary.productName,
      };
    }
);

export const getIsBankingRateDisplayed = createSelector(get, (configState) => {
  return configState?.some(
    (configuration: Configuration) =>
      configuration?.key === ConfigurationKey.WHITELABEL_LANDING_PAGE_SHOW_BANKING_RATE &&
      isConfigurationValueTrue(configuration)
  );
});

export const getIsBrokerageRateDisplayed = createSelector(get, (configState) => {
  return configState?.some(
    (configuration: Configuration) =>
      configuration?.key === ConfigurationKey.WHITELABEL_LANDING_PAGE_SHOW_BROKERAGE_RATE &&
      isConfigurationValueTrue(configuration)
  );
});

export const getIsContactConsentDisplayed = createSelector(get, (configState) => {
  return configState?.some(
    (configuration: Configuration) =>
      configuration?.key === ConfigurationKey.WHITELABEL_SHOW_CONTACT_CONSENT &&
      isConfigurationValueTrue(configuration)
  );
});

export const getIsLanguageToggleDisplayed = createSelector(get, (configState) => {
  return configState?.some(
    (configuration: Configuration) =>
      configuration?.key === ConfigurationKey.WHITELABEL_SHOW_LANGUAGE_TOGGLE &&
      isConfigurationValueTrue(configuration)
  );
});
